import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  getDoc,
  addDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
  setDoc
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';
import { Proposal } from '../models/Proposal';
import { User } from '../models/User';
import { PublicUser } from '../models/PublicUser';
import { CustomMeal } from '../models/CustomMeal';
import { Restaurant } from '../models/Restaurant';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { debug } from '../utils/debug';

// Constants
const BASE_URL = 'https://us-central1-didema-d76c4.cloudfunctions.net';
const BUSINESS_GET_PATH = '/businessget/';
const CACHE_DURATION_MILLIS = 24 * 60 * 60 * 1000; // 24 hours

// Add token cache at the top of the file with other constants
let tokenCache = {
  token: null,
  expirationTime: null
};

// Standard meals
const STANDARD_BREAKFAST = new CustomMeal(
    "Breakfast:1:sunrise.fill:donuts,waffles,baguettes,breakfast_brunch,coffee,coffeeshops,danish,creperies,bakeries"
);

const STANDARD_LUNCH = new CustomMeal(
    "Lunch:2:sun.max.fill:restaurants,foodtrucks,poke,sportsbars,irish_pubs,bento,pretzels"
);

const STANDARD_DINNER = new CustomMeal(
    "Dinner:3:sunset.fill:restaurants,poke,sportsbars,irish_pubs,bento,beergarden,pubs"
);

// Known meals map
const knownCustomMeals = new Map();
knownCustomMeals.set('1', STANDARD_BREAKFAST);
knownCustomMeals.set('2', STANDARD_LUNCH);
knownCustomMeals.set('3', STANDARD_DINNER);

// Available custom meals for the current user
export const availableCustomMeals = [];

// Helper function to collect custom meals from a user
const collectCustomMeals = (user) => {
  if (!user) return;

  const customMeals = user.getCustomMeals();
  if (customMeals && customMeals.length > 0) {
    customMeals.forEach(meal => {
      if (meal && meal.id) {
        knownCustomMeals.set(meal.id, meal);
      }
    });
  }

  // If this is the current user, update availableCustomMeals
  const currentUser = getCurrentUser();
  if (currentUser && user.id === currentUser.uid) {
    // Clear and reset available meals
    availableCustomMeals.length = 0;
    
    // Add standard meals
    availableCustomMeals.push(STANDARD_BREAKFAST);
    availableCustomMeals.push(STANDARD_LUNCH);
    availableCustomMeals.push(STANDARD_DINNER);
    
    // Add user's custom meals
    if (customMeals && customMeals.length > 0) {
      availableCustomMeals.push(...customMeals);
    }
  }
};

// Helper function to get known custom meal
export const getKnownCustomMeal = (mealId) => {
  return knownCustomMeals.get(mealId) || null;
};

// Helper function to get all known custom meals
export const getKnownCustomMeals = () => {
  return new Map(knownCustomMeals);
};

// Helper function to get current user
const getCurrentUser = () => {
  const auth = getAuth();
  return auth.currentUser;
};

// Helper function to get event time
const getEventTime = (proposal) => {
  if (!proposal) return null;
  const time = proposal.eventtime;
  if (!time) {
    debug.error('Invalid time value:', time);
    return null;
  }
  try {
    return new Date(time);
  } catch (error) {
    console.error('Invalid time value:', time);
    return null;
  }
};

// Categorize proposals into future, recent, and past
export const categorizeProposals = (proposals) => {
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
  
  debug.log('Current time:', now.toLocaleString());
  debug.log('24 hours ago:', twentyFourHoursAgo.toLocaleString());
  debug.log('2 weeks ago:', twoWeeksAgo.toLocaleString());

  const future = proposals
    .filter(p => {
      if (!(p instanceof Proposal)) {
        debug.error('Invalid proposal instance:', p);
        return false;
      }
      const eventTime = getEventTime(p);
      if (!eventTime) return false;
      const isFuture = eventTime >= twentyFourHoursAgo;
      return isFuture;
    })
    .sort((a, b) => {
      const timeA = getEventTime(a);
      const timeB = getEventTime(b);
      return timeB - timeA; // Sort future events in descending order (most future first)
    });

  const recent = proposals
    .filter(p => {
      if (!(p instanceof Proposal)) {
        debug.error('Invalid proposal instance:', p);
        return false;
      }
      const eventTime = getEventTime(p);
      if (!eventTime) return false;
      const isRecent = eventTime < twentyFourHoursAgo && eventTime >= twoWeeksAgo;
      return isRecent;
    })
    .sort((a, b) => {
      const timeA = getEventTime(a);
      const timeB = getEventTime(b);
      return timeB - timeA; // Sort recent events in descending order (most recent first)
    });

  const past = proposals
    .filter(p => {
      if (!(p instanceof Proposal)) {
        debug.error('Invalid proposal instance:', p);
        return false;
      }
      const eventTime = getEventTime(p);
      if (!eventTime) return false;
      const isPast = eventTime < twoWeeksAgo;
      return isPast;
    })
    .sort((a, b) => {
      const timeA = getEventTime(a);
      const timeB = getEventTime(b);
      return timeB - timeA; // Sort past events in descending order (most recent first)
    });

  debug.log('Categorized results:', {
    future: future.map(p => ({ id: p.id, time: p.eventtime, description: p.generateDescription() })),
    recent: recent.map(p => ({ id: p.id, time: p.eventtime, description: p.generateDescription() })),
    past: past.map(p => ({ id: p.id, time: p.eventtime, description: p.generateDescription() }))
  });

  return { future, recent, past };
};

// Proposals
export const getProposals = async (userId) => {
  debug.log('getProposals called with userId:', userId);
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) {
    debug.error('User document not found for ID:', userId);
    throw new Error('User not found');
  }

  const userData = userSnap.data();
  debug.log('User data:', userData);
  const proposalIds = userData.joinedproposals || [];
  debug.log('Proposal IDs from user data:', proposalIds);
  
  const proposals = await Promise.all(
    proposalIds.map(async (id) => {
      debug.log('Fetching proposal:', id);
      const proposalRef = doc(db, 'proposals', id);
      try {
        const proposalSnap = await getDoc(proposalRef);
        if (proposalSnap.exists()) {
          const proposalData = {
            id: proposalSnap.id,
            ...proposalSnap.data()
          };
          debug.log('Created proposal instance with data:', proposalData);
          return new Proposal(proposalData);
        }
        debug.log('Proposal not found:', id);
        return null;
      } catch (error) {
        //console.error('Error fetching proposal:', id, error);
        if (error.code === 'object-not-found') {
          debug.log('Removing user from proposal due to object not found:', id);
          try {
            await removeProposalMember(id, userId);
          } catch (removeError) {
            debug.error('Error removing user from proposal:', id, removeError);
          }
        }
        else {
          debug.error('Error fetching proposal:', id, error);
        }
        return null;
      }
    })
  );

  const validProposals = proposals.filter(p => p !== null);
  debug.log('Final valid proposals:', validProposals);
  return validProposals;
};

export const getProposal = async (proposalId) => {
  const proposalRef = doc(db, 'proposals', proposalId);
  const proposalSnap = await getDoc(proposalRef);
  
  if (proposalSnap.exists()) {
    return new Proposal({
      id: proposalSnap.id,
      ...proposalSnap.data()
    });
  }
  return null;
};

export const createProposal = async (proposalData) => {
  try {
    const functions = getFunctions();
    const createProposalFunction = httpsCallable(functions, 'createproposal');
    
    debug.log('Calling createproposal function with data:', proposalData);
    const result = await createProposalFunction({ proposalData });
    debug.log('Proposal created result:', result);
    
    return new Proposal(result.data);
  } catch (error) {
    debug.error('Error creating proposal:', error);
    throw error;
  }
};

export const joinProposal = async (proposalId, userId) => {
  const proposalRef = doc(db, 'proposals', proposalId);
  const proposalSnap = await getDoc(proposalRef);
  
  if (!proposalSnap.exists()) {
    throw new Error('Proposal not found');
  }

  const proposalData = proposalSnap.data();
  if (!proposalData.joined.includes(userId)) {
    await updateDoc(proposalRef, {
      joined: arrayUnion(userId)
    });
  }

  return new Proposal({
    id: proposalSnap.id,
    ...proposalData,
    joined: [...proposalData.joined, userId]
  });
};

export const removeProposalMember = async (proposalId, userId) => {
  try {
    debug.log('Leaving proposal:', proposalId, 'for user:', userId);
    const functions = getFunctions();
    const removeProposalMemberFunction = httpsCallable(functions, 'removeproposalmember');
    
    const result = await removeProposalMemberFunction({
      proposalid: proposalId,
      userid: userId
    });
    
    return result.data;
  } catch (error) {
    debug.error('Error leaving proposal:', error);
    throw error;
  }
};

// Users
export const getUser = async (userId) => {
  debug.log('Attempting to get user document for ID:', userId);
  const docRef = doc(db, 'users', userId);
  try {
    const docSnap = await getDoc(docRef);
    debug.log('Document exists:', docSnap.exists());
    if (docSnap.exists()) {
      const userData = docSnap.data();
      debug.log('User data retrieved:', { id: docSnap.id, ...userData });
      const user = new User({
        id: docSnap.id,
        ...userData
      });

      // Collect custom meals from the user
      collectCustomMeals(user);

      return user;
    }
    debug.log('User document does not exist');
    return null;
  } catch (error) {
    debug.error('Error in getUser:', {
      code: error.code,
      message: error.message,
      userId: userId,
      auth: getAuth().currentUser?.uid
    });
    throw error;
  }
};

export const getPublicUser = async (userId) => {
  const docRef = doc(db, 'userspublic', userId);
  debug.log('Getting public user:', userId);
  const docSnap = await getDoc(docRef);
  debug.log('Public user:', docSnap.data());
  if (docSnap.exists()) {
    return new PublicUser({
      id: docSnap.id,
      ...docSnap.data()
    });
  }
  return null;
};

// Add listener functions
export const listenToUser = (userId, onUpdate) => {
  debug.log('Setting up listener for user:', userId);
  const userRef = doc(db, 'users', userId);
  return onSnapshot(userRef, async (docSnap) => {
    if (docSnap.exists()) {
      const userData = {
        id: docSnap.id,
        ...docSnap.data()
      };
      debug.log('User data updated:', userData);
      
      // Create a new User instance
      const user = new User(userData);
      
      // Fetch and instantiate all proposals
      const proposalIds = userData.joinedproposals || [];
      debug.log('Fetching proposals for user:', { userId, proposalIds });
      
      const proposals = await Promise.all(
        proposalIds.map(async (id) => {
          try {
            const proposalRef = doc(db, 'proposals', id);
            const proposalSnap = await getDoc(proposalRef);
            if (proposalSnap.exists()) {
              const proposalData = {
                id: proposalSnap.id,
                ...proposalSnap.data()
              };
              debug.log('Created proposal instance:', { id: proposalData.id });
              return new Proposal(proposalData);
            }
            debug.log('Proposal not found:', id);
            return null;
          } catch (error) {
            debug.error('Error fetching proposal:', { id, error });
            return null;
          }
        })
      );
      
      // Filter out null proposals
      const validProposals = proposals.filter(p => p !== null);
      debug.log('Valid proposals found:', validProposals.map(p => p.id));
      
      // Update the user with the valid proposals
      user.setJoinedProposals(validProposals.map(p => p.id));
      
      onUpdate(user);
    } else {
      debug.log('User document does not exist');
      onUpdate(null);
    }
  }, (error) => {
    debug.error('Error in user listener:', error);
  });
};

export const listenToProposal = (proposalId, onUpdate) => {
  debug.log('Setting up listener for proposal:', proposalId);
  const proposalRef = doc(db, 'proposals', proposalId);
  return onSnapshot(proposalRef, (doc) => {
    if (doc.exists()) {
      const proposalData = {
        id: doc.id,
        ...doc.data()
      };
      debug.log('Proposal data updated:', proposalData);
      onUpdate(new Proposal(proposalData));
    } else {
      debug.log('Proposal document does not exist');
      onUpdate(null);
    }
  }, (error) => {
    debug.error('Error in proposal listener:', error);
  });
};

export const listenToPublicUser = (userId, onUpdate) => {
  debug.log('Setting up listener for public user:', userId);
  const userRef = doc(db, 'userspublic', userId);
  return onSnapshot(userRef, (doc) => {
    if (doc.exists()) {
      const userData = {
        id: doc.id,
        ...doc.data()
      };
      debug.log('Public user data updated:', userData);
      onUpdate(new PublicUser(userData));
    } else {
      debug.log('Public user document does not exist');
      onUpdate(null);
    }
  }, (error) => {
    debug.error('Error in public user listener:', error);
  });
};

export const listenToPublicUsers = (userIds, onUpdate) => {
  debug.log('Setting up listeners for public users:', userIds);
  const unsubscribes = new Map();

  userIds.forEach(userId => {
    const userRef = doc(db, 'userspublic', userId);
    const unsubscribe = onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        const userData = {
          id: doc.id,
          ...doc.data()
        };
        debug.log('Public user data updated:', userData);
        onUpdate(new PublicUser(userData));
      }
    }, (error) => {
      debug.error('Error in public user listener:', error);
    });
    unsubscribes.set(userId, unsubscribe);
  });

  // Return a function to clean up all listeners
  return () => {
    unsubscribes.forEach(unsubscribe => unsubscribe());
    unsubscribes.clear();
  };
};

// Proposal Management
export const proposalStartVoting = async (proposalId) => {
  try {
    const response = await fetch(`${BASE_URL}/startVoting`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`
      },
      body: JSON.stringify({
        data: { proposalId }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to start voting: ${response.status}`);
    }

    const data = await response.json();
    return new Proposal(data.data);
  } catch (error) {
    console.error('Error starting voting:', error);
    throw error;
  }
};

export const proposalSetState = async (proposalId, state) => {
  try {
    const response = await fetch(`${BASE_URL}/setproposalstate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`
      },
      body: JSON.stringify({
        data: {
          id: proposalId,
          state: state.toLowerCase()
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to set proposal state: ${response.status}`);
    }

    const data = await response.json();
    return new Proposal(data.data);
  } catch (error) {
    console.error('Error setting proposal state:', error);
    throw error;
  }
};

export const proposalSetMeal = async (proposalId, customMeal) => {
  try {
    const response = await fetch(`${BASE_URL}/setproposalmeal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`
      },
      body: JSON.stringify({
        data: {
          id: proposalId,
          meal: customMeal.toString()
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to set proposal meal: ${response.status}`);
    }

    const data = await response.json();
    return new Proposal(data.data);
  } catch (error) {
    console.error('Error setting proposal meal:', error);
    throw error;
  }
};

export const setProposalPublic = async (proposalId, isPublic) => {
  try {
    const response = await fetch(`${BASE_URL}/setproposalpublic`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`
      },
      body: JSON.stringify({
        data: {
          id: proposalId,
          public: isPublic
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to set proposal public status: ${response.status}`);
    }

    const data = await response.json();
    return new Proposal(data.data);
  } catch (error) {
    console.error('Error setting proposal public status:', error);
    throw error;
  }
};

export const setProposalSorted = async (proposalId, sortedRestaurantIds, totalCompares) => {
  try {
    const response = await fetch(`${BASE_URL}/setproposalsorted`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`
      },
      body: JSON.stringify({
        data: {
          id: proposalId,
          sorted: sortedRestaurantIds,
          totalcompares: totalCompares
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to set proposal sorted list: ${response.status}`);
    }

    const data = await response.json();
    return new Proposal(data.data);
  } catch (error) {
    console.error('Error setting proposal sorted list:', error);
    throw error;
  }
};

export const decideProposal = async (proposalId) => {
  try {
    const response = await fetch(`${BASE_URL}/makeDecision`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getAuthToken()}`
      },
      body: JSON.stringify({
        data: { proposalId }
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to make decision: ${response.status}`);
    }

    const data = await response.json();
    return new Proposal(data.data);
  } catch (error) {
    debug.error('Error making decision:', error);
    throw error;
  }
};

export const savePublicUserDisplayName = async (displayName) => {
  const userId = getCurrentUser().uid;
  console.log('Saving display name:', displayName);
  try {
    await updateDoc(doc(db, 'userspublic', userId), {
      displayname: displayName
    });
    const userDoc = await getDoc(doc(db, 'userspublic', userId));
    return new PublicUser({
      id: userDoc.id,
      ...userDoc.data()
    });
  } catch (error) {
    console.error('Error saving display name:', error);
    throw error;
  }
};

export const saveProfilePicture = async (imageData) => {
  console.log('Saving profile picture');
  const userId = getCurrentUser().uid;
  console.log('Current user ID:', userId);
  
  const storage = getStorage();
  const path = `users/${userId}/profilePicture.png`;
  console.log('Storage path:', path);
  
  const storageRef = ref(storage, path);
  console.log('Full storage reference:', storageRef.fullPath);
  
  try {
    console.log('Attempting to upload bytes...');
    const snapshot = await uploadBytes(storageRef, imageData);
    console.log('Upload successful, getting download URL...');
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('Download URL obtained:', downloadURL);
    
    // Update the public user document with the new avatar URL
    await updateDoc(doc(db, 'userspublic', userId), {
      avatarurl: downloadURL
    });
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      serverResponse: error.serverResponse
    });
    throw error;
  }
};

// Restaurant Management
export const setRestaurantFavorite = async (restaurantId, isFavorite) => {
  const userId = getCurrentUser().uid;
  const userRef = doc(db, 'userspublic', userId);
  
  try {
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    const favorites = userData.favoriterestaurants || [];
    
    if (isFavorite) {
      if (!favorites.includes(restaurantId)) {
        await updateDoc(userRef, {
          favoriterestaurants: arrayUnion(restaurantId)
        });
      }
      
      // Remove from blocked and trashed
      const mealBlocked = userData.mealBlockedRestaurants || {};
      for (const [mealId, blocked] of Object.entries(mealBlocked)) {
        if (blocked.includes(restaurantId)) {
          await updateDoc(userRef, {
            [`removedformeal-${mealId}`]: arrayRemove(restaurantId)
          });
        }
      }
      
      const trashed = userData.trashedrestaurants || [];
      if (trashed.includes(restaurantId)) {
        await updateDoc(userRef, {
          trashedrestaurants: arrayRemove(restaurantId)
        });
      }
    } else {
      if (favorites.includes(restaurantId)) {
        await updateDoc(userRef, {
          favoriterestaurants: arrayRemove(restaurantId)
        });
      }
    }

    const updatedUserDoc = await getDoc(userRef);
    return new PublicUser({
      id: updatedUserDoc.id,
      ...updatedUserDoc.data()
    });
  } catch (error) {
    console.error('Error updating favorite restaurants:', error);
    throw error;
  }
};

export const setRestaurantBlocked = async (restaurantId, isBlocked, meal) => {
  const userId = getCurrentUser().uid;
  const userRef = doc(db, 'userspublic', userId);
  
  try {
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    if (isBlocked) {
      // Remove from favorites
      const favorites = userData.favoriterestaurants || [];
      if (favorites.includes(restaurantId)) {
        await updateDoc(userRef, {
          favoriterestaurants: arrayRemove(restaurantId)
        });
      }
      
      if (meal) {
        const fieldName = `removedformeal-${meal.id}`;
        const mealBlocked = userData[fieldName] || [];
        if (!mealBlocked.includes(restaurantId)) {
          await updateDoc(userRef, {
            [fieldName]: arrayUnion(restaurantId)
          });
        }
      } else {
        const trashed = userData.trashedrestaurants || [];
        if (!trashed.includes(restaurantId)) {
          await updateDoc(userRef, {
            trashedrestaurants: arrayUnion(restaurantId)
          });
        }
      }
    } else {
      if (meal) {
        const fieldName = `removedformeal-${meal.id}`;
        const mealBlocked = userData[fieldName] || [];
        if (mealBlocked.includes(restaurantId)) {
          await updateDoc(userRef, {
            [fieldName]: arrayRemove(restaurantId)
          });
        }
      }
      
      const trashed = userData.trashedrestaurants || [];
      if (trashed.includes(restaurantId)) {
        await updateDoc(userRef, {
          trashedrestaurants: arrayRemove(restaurantId)
        });
      }
    }

    const updatedUserDoc = await getDoc(userRef);
    return new PublicUser({
      id: updatedUserDoc.id,
      ...updatedUserDoc.data()
    });
  } catch (error) {
    console.error('Error updating blocked restaurants:', error);
    throw error;
  }
};

// Helper function to get auth token
const getAuthToken = async (maxRetries = 3) => {
  // Check if we have a valid cached token
  const now = Date.now();
  if (tokenCache.token && tokenCache.expirationTime && now < tokenCache.expirationTime - 60000) {
    // Return cached token if it's still valid (with 1 minute buffer)
    return tokenCache.token;
  }

  for (let i = 0; i < maxRetries; i++) {
    try {
      const user = getCurrentUser();
      if (!user) {
        throw new Error('No user logged in');
      }
      
      // Try to get the token with a small delay between retries
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      const token = await user.getIdToken(false); // Don't force refresh unless necessary
      if (!token) {
        throw new Error('Failed to get valid token');
      }

      // Cache the token and set expiration to 55 minutes from now
      // (Firebase tokens last 1 hour, we set it slightly shorter to be safe)
      tokenCache = {
        token,
        expirationTime: now + (55 * 60 * 1000)
      };
      
      return token;
    } catch (error) {
      debug.error(`Attempt ${i + 1} to get auth token failed:`, error);
      if (i === maxRetries - 1) {
        throw new Error(`Failed to get auth token after ${maxRetries} attempts: ${error.message}`);
      }
    }
  }
};

export const getRestaurant = async (restaurantId, onUpdate = null) => {
  if (!restaurantId || restaurantId.trim() === '') {
    console.error('Cannot get restaurant - empty restaurant ID provided');
    return null;
  }

  try {
    const restaurantRef = doc(db, 'restaurants', restaurantId);
    const restaurantSnap = await getDoc(restaurantRef);

    if (restaurantSnap.exists()) {
      const data = restaurantSnap.data();
      const cacheTime = data.cachetime;
      const restaurantData = data.restaurantdata;

      try {
        const jsonData = JSON.parse(restaurantData);
        const restaurant = new Restaurant({
          id: restaurantId,
          name: jsonData.name,
          alias: jsonData.alias,
          imageUrl: jsonData.image_url,
          isClosed: jsonData.is_closed,
          url: jsonData.url,
          reviewCount: jsonData.review_count,
          rating: jsonData.rating,
          price: jsonData.price,
          phone: jsonData.phone,
          displayPhone: jsonData.display_phone,
          distance: jsonData.distance,
          coordinates: jsonData.coordinates,
          location: jsonData.location,
          categories: jsonData.categories,
          transactions: jsonData.transactions
        });

        // If cache is expired and we have an update callback, fetch fresh data in background
        if ((!cacheTime || Date.now() - cacheTime > CACHE_DURATION_MILLIS) && onUpdate) {
          fetchFromFirebaseFunction(restaurantId)
            .then(freshRestaurant => {
              if (freshRestaurant) {
                onUpdate(freshRestaurant);
              }
            })
            .catch(error => {
              console.error('Error fetching fresh restaurant data:', error);
            });
        }

        return restaurant;
      } catch (error) {
        console.error('Error parsing restaurant data:', error);
        // If we can't parse the cached data, we need to fetch fresh data
        return fetchFromFirebaseFunction(restaurantId);
      }
    } else {
      // No cached data exists, fetch fresh data
      return fetchFromFirebaseFunction(restaurantId);
    }
  } catch (error) {
    console.error('Error fetching restaurant from Firestore:', error);
    return fetchFromFirebaseFunction(restaurantId);
  }
};

const fetchFromFirebaseFunction = async (restaurantId) => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('No user logged in');
    }

    const idToken = await user.getIdToken(true);
    const url = `${BASE_URL}${BUSINESS_GET_PATH}${restaurantId}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch restaurant data: ${response.status}`);
    }

    const restaurantData = await response.json();
    const restaurant = new Restaurant(restaurantData);
    return restaurant;
  } catch (error) {
    console.error('Error fetching from Firebase function:', error);
    throw error;
  }
};

export const inviteFriends = async (proposal, friendIds) => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    // Get the current user's public data for display name
    const publicUser = await getPublicUser(currentUser.uid);
    if (!publicUser) {
      throw new Error('Could not get user public data');
    }

    // Call the invite function for each friend
    const results = await Promise.all(
      Array.from(friendIds).map(async (friendId) => {
        const response = await fetch(`${BASE_URL}/invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await getAuthToken()}`
          },
          body: JSON.stringify({
            data: {
              invitee: friendId,
              proposalid: proposal.id,
              displayname: publicUser.displayname,
              notificationdescription: `Join me for ${proposal.getMealName()} at ${proposal.generateDescription()}!`
            }
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to invite friend: ${response.status}`);
        }

        const data = await response.json();
        return new Proposal(data.data);
      })
    );

    // Return the last updated proposal (they should all be the same)
    return results[results.length - 1];
  } catch (error) {
    console.error('Error inviting friends:', error);
    throw error;
  }
};

export const deleteAccount = async () => {
  const user = getCurrentUser();
  if (!user) {
    throw new Error('No user logged in');
  }

  try {
    // Delete from userspublic collection
    await deleteDoc(doc(db, 'userspublic', user.uid));
    
    // After successful deletion from Firestore, delete the auth account
    await user.delete();
  } catch (error) {
    console.error('Error deleting account:', error);
    throw error;
  }
};

export const removeFromFriends = async (userId) => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('No user logged in');
  }

  try {
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, {
      friends: arrayRemove(userId)
    });
    
    // Also update the public user document
    const publicUserRef = doc(db, 'userspublic', currentUser.uid);
    await updateDoc(publicUserRef, {
      friends: arrayRemove(userId)
    });
  } catch (error) {
    console.error('Error removing from friends:', error);
    throw error;
  }
};

export const removeFromBlocked = async (userId) => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('No user logged in');
  }

  try {
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, {
      blockedusers: arrayRemove(userId)
    });
    
    // Also update the public user document
    const publicUserRef = doc(db, 'userspublic', currentUser.uid);
    await updateDoc(publicUserRef, {
      blockedusers: arrayRemove(userId)
    });
  } catch (error) {
    console.error('Error removing from blocked:', error);
    throw error;
  }
};

export const deletePastProposals = async () => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('No user logged in');
  }

  try {
    // Get all user's proposals
    const proposals = await getProposals(currentUser.uid);
    
    // Calculate the cutoff time (24 hours ago)
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    // Filter for past proposals
    const pastProposals = proposals.filter(p => {
      const eventTime = getEventTime(p);
      return eventTime && eventTime < twentyFourHoursAgo;
    });

    console.log(`Found ${pastProposals.length} past proposals to delete`);

    // Remove user from each past proposal
    await Promise.all(
      pastProposals.map(proposal => 
        removeProposalMember(proposal.id, currentUser.uid)
      )
    );

    console.log('Successfully removed user from all past proposals');
  } catch (error) {
    console.error('Error deleting past proposals:', error);
    throw error;
  }
};

export const createCustomMeal = async (mealName, mealData) => {
  try {
    const functions = getFunctions();
    const createCustomMealFunction = httpsCallable(functions, 'createCustomMeal');
    
    // Parse the meal data string to get components
    const [name, id, icon, categories] = mealData.split(':');
    
    console.log('Calling createCustomMeal function with:', { name, icon, categories });
    const result = await createCustomMealFunction({ 
      name,
      icon,
      categories
    });
    console.log('Custom meal created result:', result);
    
    // The cloud function returns the updated user data
    const updatedUser = new User(result.data);
    
    // Collect custom meals from the updated user
    collectCustomMeals(updatedUser);
    
    return updatedUser;
  } catch (error) {
    console.error('Error creating custom meal:', error);
    throw error;
  }
}; 